import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { EmailAddress, PhoneNumber } from "@/features/customers/types";
import { TrashIcon } from "@phosphor-icons/react";
import { useTranslation } from "@/features/utils/i18n";

interface EmailItemProps {
  email: EmailAddress;
  onUpdate: (updates: Partial<EmailAddress>) => void;
  onRemove: () => void;
  canRemove: boolean;
}

export function EmailItem({
  email,
  onUpdate,
  onRemove,
  canRemove,
}: EmailItemProps) {
  const { t } = useTranslation("customers");
  return (
    <div className="flex gap-2">
      <div className="flex-1 space-y-2">
        <Input
          type="email"
          value={email.address}
          onChange={(e) => onUpdate({ address: e.target.value })}
          placeholder={t("form.placeholders.email")}
        />
      </div>
      <div className="w-32">
        <Select
          value={email.type || "other"}
          onValueChange={(value) =>
            onUpdate({ type: value as EmailAddress["type"] })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="home">{t("contactTypes.email.home")}</SelectItem>
            <SelectItem value="work">{t("contactTypes.email.work")}</SelectItem>
            <SelectItem value="other">
              {t("contactTypes.email.other")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant={email.primary ? "default" : "outline"}
          size="sm"
          onClick={() => onUpdate({ primary: !email.primary })}
          title={email.primary ? t("actions.primary") : t("actions.setPrimary")}
        >
          {email.primary ? t("actions.primary") : t("actions.setPrimary")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          disabled={!canRemove}
        >
          <TrashIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

interface PhoneItemProps {
  phone: PhoneNumber;
  onUpdate: (updates: Partial<PhoneNumber>) => void;
  onRemove: () => void;
  canRemove: boolean;
  PhoneInputComponent: React.ComponentType<{
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  }>;
}

export function PhoneItem({
  phone,
  onUpdate,
  onRemove,
  canRemove,
  PhoneInputComponent,
}: PhoneItemProps) {
  const { t } = useTranslation("customers");
  return (
    <div className="flex gap-2">
      <div className="flex-1">
        <PhoneInputComponent
          value={phone.number}
          onChange={(number: string) => onUpdate({ number: number || "" })}
          placeholder={t("form.placeholders.phone")}
        />
      </div>
      <div className="w-32">
        <Select
          value={phone.type || "mobile"}
          onValueChange={(value) =>
            onUpdate({ type: value as PhoneNumber["type"] })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="mobile">
              {t("contactTypes.phone.mobile")}
            </SelectItem>
            <SelectItem value="home">{t("contactTypes.phone.home")}</SelectItem>
            <SelectItem value="work">{t("contactTypes.phone.work")}</SelectItem>
            <SelectItem value="fax">{t("contactTypes.phone.fax")}</SelectItem>
            <SelectItem value="other">
              {t("contactTypes.phone.other")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant={phone.primary ? "default" : "outline"}
          size="sm"
          onClick={() => onUpdate({ primary: !phone.primary })}
          title={phone.primary ? t("actions.primary") : t("actions.setPrimary")}
        >
          {phone.primary ? t("actions.primary") : t("actions.setPrimary")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          disabled={!canRemove}
        >
          <TrashIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
